import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '../services/api'
import type { GmailIntegration } from '../types'

export interface UserProfile {
  id: string
  email: string
  firstName: string
  lastName: string
  avatar?: string
  role?: string
  businessName?: string
}

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Check if user has valid token
  useEffect(() => {
    // Check for token in URL parameters first (for cross-app authentication)
    const params = new URLSearchParams(window.location.search)
    const tokenFromUrl = params.get('token')

    if (tokenFromUrl) {
      try {
        localStorage.setItem('token', tokenFromUrl)
        sessionStorage.removeItem('token')
      } catch {
        try {
          sessionStorage.setItem('token', tokenFromUrl)
        } catch {
          // ignore
        }
      }
      // Clean up URL (keep path so /inbox SSO stays on inbox)
      window.history.replaceState({}, '', window.location.pathname || '/')
    }

    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    setIsAuthenticated(!!token)
    setIsLoading(false)
  }, [])

  // Current user profile (for avatar, name)
  const { data: profile } = useQuery<UserProfile>({
    queryKey: ['auth-profile'],
    queryFn: async () => {
      const response = await apiClient.get('/auth/profile')
      return response.data
    },
    enabled: isAuthenticated,
  })

  // Check Gmail connection status
  const { data: gmailStatus } = useQuery<GmailIntegration>({
    queryKey: ['gmail-status'],
    queryFn: async () => {
      const response = await apiClient.get('/gmail/status')
      return response.data
    },
    enabled: isAuthenticated,
  })

  return {
    isAuthenticated,
    isLoading,
    profile: profile ?? null,
    gmailConnected: gmailStatus?.isConnected || false,
    emailAddress: gmailStatus?.emailAddress,
    isAIEnabled: gmailStatus?.isAIEnabled || false,
  }
}

